import { createContext, useCallback, useContext, useEffect, useLayoutEffect, useState } from 'react';
import { useIsPresent } from 'motion/react';

/** A back affordance is either a route (`to`) or a step change (`onClick`). */
export type OnboardingBack = { label: string } & (
  { to: string; onClick?: never } | { onClick: () => void; to?: never }
);

type Publish = (back: OnboardingBack | null) => void;

/**
 * The onboarding chrome — photo stage, header and frosted card — is mounted
 * once by `routes/onboarding-layout.tsx` and outlives every step, so a screen
 * can no longer render its own back button: the header belongs to the layout.
 *
 * This channel is how a screen still owns the decision. It publishes the back
 * target it wants and the persistent header renders it. Nothing else about the
 * chrome is dynamic — the layout derives the card material, alignment and logo
 * from the path — and the header's *presence* does not depend on this, so a
 * server render is never missing a row.
 */
const BackChannel = createContext<Publish | null>(null);

export const OnboardingBackProvider = BackChannel.Provider;

/**
 * `useLayoutEffect` on the client so a published target is flushed before the
 * browser paints the arriving step, and `useEffect` on the server, where a
 * layout effect only warns.
 */
const useChromeEffect = typeof document === 'undefined' ? useEffect : useLayoutEffect;

/**
 * Two targets are the same affordance when they point at the same place. The
 * comparison is what lets a screen pass a fresh object on every render without
 * looping: an `onClick` target must therefore be a stable callback.
 */
function sameBack(a: OnboardingBack | null, b: OnboardingBack | null) {
  if (a === null || b === null) {
    return a === b;
  }

  return a.label === b.label && a.to === b.to && a.onClick === b.onClick;
}

/**
 * Declares this screen's back affordance in the persistent header. Call it
 * unconditionally, with `null` for a screen that has none — the last screen to
 * publish wins, so a screen that stays quiet would inherit its predecessor's.
 *
 * A screen that is *leaving* is skipped: for the ~160 ms of a step crossing
 * both screens are mounted and rendering, and the one on its way out must not
 * take the header back off the one arriving.
 */
export function useOnboardingBack(back: OnboardingBack | null) {
  const publish = useContext(BackChannel);
  const isPresent = useIsPresent();

  // No dependency list on purpose: the publisher below de-duplicates by value,
  // which is cheaper than asking every screen to memoise its target.
  useChromeEffect(() => {
    if (isPresent) {
      publish?.(back);
    }
  });
}

/** The layout side of the channel: the current target, and the publisher. */
export function useOnboardingBackChannel() {
  const [back, setBack] = useState<OnboardingBack | null>(null);

  const publish = useCallback<Publish>((next) => setBack((prev) => (sameBack(prev, next) ? prev : next)), []);

  return { back, publish };
}
